import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { type WorkflowStep } from '@/contexts/WorkflowContext';
import { getStepConfig } from '@/config/workflowSteps';

interface StepHeaderProps {
  step: WorkflowStep;
  /** Show expanded details by default */
  defaultExpanded?: boolean;
}

/**
 * StepHeader provides contextual guidance for each workflow step.
 * Designed for novice/intermediate users to understand what to focus on.
 */
const StepHeader: React.FC<StepHeaderProps> = ({
  step,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const config = getStepConfig(step);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        bgcolor: 'primary.50',
        borderLeft: 4,
        borderColor: 'primary.main',
      }}
    >
      {/* Main header - always visible */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <LightbulbOutlinedIcon
          sx={{ color: 'primary.main', mt: 0.25, fontSize: '1.25rem' }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: 'primary.dark', lineHeight: 1.3 }}
          >
            {config.description}
          </Typography>

          {/* Focus points - always visible */}
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Focus on:
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                mt: 0.5,
                pl: 2.5,
                '& li': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  lineHeight: 1.6,
                  mb: 0.25,
                },
              }}
            >
              {config.focusPoints.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Expand/collapse toggle */}
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ mt: -0.5 }}
          aria-label={expanded ? 'Show less' : 'Show more'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Collapsible section */}
      <Collapse in={expanded}>
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {/* Don't worry about */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Don&apos;t worry about yet:
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {config.dontWorryAbout.map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 'auto',
                    py: 0.5,
                    color: 'text.secondary',
                    borderColor: 'grey.300',
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Output description */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              What you&apos;ll have when done:
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
              {config.outputDescription}
            </Typography>
          </Box>

          {/* Next step preview */}
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            {config.nextStepPreview}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default StepHeader;
